import { useState, useEffect, useCallback, useRef } from 'react';
import { X, GripVertical, ChevronUp, ChevronDown, Type, MousePointerClick, ListOrdered } from 'lucide-react';
import { useStudioStore } from '../../../stores/studioStore';
import { reconcileReadingOrder, isReadableElement } from '../../../lib/readingOrder';
import type { CanvasElement } from '../../../types/index';

interface ReadingOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface OrderRow {
  id: string;
  element: CanvasElement;
  isNew: boolean;
}

function getRowLabel(el: CanvasElement): string {
  const text = (el as { text?: string }).text;
  if (text && text.trim()) return text.trim();
  if (el.name && el.name.trim()) return el.name.trim();
  return el.type === 'button' ? 'Untitled button' : 'Untitled text';
}

export function ReadingOrderModal({ isOpen, onClose, onConfirm }: ReadingOrderModalProps) {
  const canvasState = useStudioStore((s) => s.canvasState);
  const setReadingOrder = useStudioStore((s) => s.setReadingOrder);

  const [rows, setRows] = useState<OrderRow[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const elements = canvasState.elements;
    const { order, appendedIds } = reconcileReadingOrder(canvasState.readingOrder, elements);
    const appended = new Set(appendedIds);
    const byId = new Map(elements.filter(isReadableElement).map((el) => [el.id, el]));
    setRows(
      order
        .map((id) => byId.get(id))
        .filter((el): el is CanvasElement => !!el)
        .map((el) => ({ id: el.id, element: el, isNew: appended.has(el.id) }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const moveRow = useCallback((from: number, to: number) => {
    setRows((prev) => {
      if (to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setReadingOrder(rows.map((r) => r.id));
    onConfirm();
  }, [rows, setReadingOrder, onConfirm]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 max-w-md w-full mx-4 bg-[#1a1a2e] border border-white/[0.1] rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <ListOrdered size={18} className="text-cyan-400" />
            <h2 className="text-lg font-semibold text-white font-['BioRhyme']">Reading Order</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2">
          <p className="text-xs text-white/40 leading-relaxed">
            Screen readers announce text in this order. Drag to arrange from first to last.
            This order is saved with the project and pre-fills on every export.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto sidebar-scroll px-6 py-3 space-y-1.5">
          {rows.map((row, i) => (
            <div
              key={row.id}
              draggable
              onDragStart={() => { dragIndexRef.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setDragOverIndex(i); }}
              onDragLeave={() => setDragOverIndex((d) => (d === i ? null : d))}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndexRef.current !== null) moveRow(dragIndexRef.current, i);
                dragIndexRef.current = null;
                setDragOverIndex(null);
              }}
              onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); }}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors cursor-grab active:cursor-grabbing ${
                dragOverIndex === i
                  ? 'border-cyan-500 bg-cyan-500/[0.08]'
                  : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
              }`}
            >
              <GripVertical size={14} className="text-white/20 flex-shrink-0" />
              <span className="w-5 text-center text-[11px] font-mono text-white/30 flex-shrink-0">{i + 1}</span>
              {row.element.type === 'button' ? (
                <MousePointerClick size={14} className="text-cyan-400/60 flex-shrink-0" />
              ) : (
                <Type size={14} className="text-white/40 flex-shrink-0" />
              )}
              <span className="flex-1 min-w-0 text-sm text-white/70 truncate">{getRowLabel(row.element)}</span>
              {row.isNew && (
                <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-amber-500/10 text-amber-400 flex-shrink-0">
                  New
                </span>
              )}
              {!row.element.visible && (
                <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-white/[0.04] text-white/25 flex-shrink-0">
                  Hidden
                </span>
              )}
              <div className="flex flex-col flex-shrink-0">
                <button
                  onClick={() => moveRow(i, i - 1)}
                  disabled={i === 0}
                  className="p-0.5 rounded text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-default transition-colors"
                  aria-label="Move up"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  onClick={() => moveRow(i, i + 1)}
                  disabled={i === rows.length - 1}
                  className="p-0.5 rounded text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-default transition-colors"
                  aria-label="Move down"
                >
                  <ChevronDown size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-5 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors"
          >
            Confirm Order &amp; Export
          </button>
        </div>
      </div>
    </div>
  );
}
